import React, { useState, useRef, useEffect } from "react";
import Button from "@mui/material/Button";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { styled } from "@mui/material/styles";
import { CLOTHING_CATEGORIES } from "../../constants/categories";
import styles from "./ClothingDropdown.module.css";

const DropdownButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ isActive, theme }) => ({
  width: "100%",
  height: "56px",
  textTransform: "none",
  fontWeight: 500,
  letterSpacing: "normal",
  fontSize: "0.9375rem",
  padding: "12px 8px",
  justifyContent: "center",
  borderRadius: "8px",
  "&.MuiButton-outlined": {
    border: "1px solid #0C5D53",
    color: "#0C5D53",
    backgroundColor: "white",
    "&:hover": {
      backgroundColor: "rgba(12, 93, 83, 0.04)",
      borderColor: "#0C5D53",
    },
  },
  "&.MuiButton-contained": {
    backgroundColor: "#0C5D53",
    color: "white",
    "&:hover": {
      backgroundColor: "#09413A",
    },
  },
}));

interface ClothingDropdownProps {
  selectedCategory: string;
  onSelectCategory: (category: string) => void;
  categoryCounts: {
    "Custom Hoodies": number;
    "Custom T-Shirts": number;
    "Custom Racerbacks": number;
  };
}

const ClothingDropdown: React.FC<ClothingDropdownProps> = ({
  selectedCategory: propSelectedCategory,
  onSelectCategory,
  categoryCounts = { Hoodies: 0, "T-shirts": 0, Racerbacks: 0 },
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const categoryDisplayNames = {
    "Custom Hoodies": "Custom Hoodies",
    "Custom T-Shirts": "Custom T-Shirts",
    "Custom Racerbacks": "Custom Racerbacks",
  } as const;

  const availableCategories = CLOTHING_CATEGORIES.filter(
    (category) => categoryCounts[category as keyof typeof categoryCounts] > 0
  );

  const mobileCategories = [
    "Custom Hoodies",
    "Custom T-Shirts",
    "Custom Racerbacks",
  ];

  const singleCategory =
    availableCategories.length === 1 ? availableCategories[0] : null;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (
      propSelectedCategory &&
      !availableCategories.includes(propSelectedCategory as any)
    ) {
      onSelectCategory("");
    }
  }, [availableCategories, propSelectedCategory, onSelectCategory]);

  const handleSelect = (category: string) => {
    if (propSelectedCategory === category) {
      onSelectCategory("");
    } else {
      onSelectCategory(category);
    }
    setIsOpen(false);
  };

  const renderContent = () => {
    if (singleCategory) {
      return (
        <DropdownButton
          variant={
            propSelectedCategory === singleCategory ? "contained" : "outlined"
          }
          onClick={() => handleSelect(singleCategory)}
          fullWidth
          className="py-3 px-2 rounded-3 fw-medium text-capitalize font-jakarta letter-spacing-normal"
          sx={{
            height: "56px",
            textTransform: "none",
            fontWeight: 500,
            letterSpacing: "normal",
            fontSize: "0.9375rem",
          }}
        >
          {singleCategory}
        </DropdownButton>
      );
    }

    return (
      <div className={styles.clothingDropdownContainer} ref={dropdownRef}>
        <div
          className={`${styles.desktopDropdown} ${styles.clothingDropdownButtonContainer}`}
        >
          <DropdownButton
            variant={propSelectedCategory ? "contained" : "outlined"}
            endIcon={<ArrowDropDownIcon />}
            onClick={() => setIsOpen(!isOpen)}
            className={styles.desktopDropdown}
            isActive={!!propSelectedCategory}
          >
            {propSelectedCategory
              ? categoryDisplayNames[
                  propSelectedCategory as keyof typeof categoryDisplayNames
                ] || propSelectedCategory
              : "Custom Apparel"}
          </DropdownButton>
          {isOpen && (
            <div className={styles.clothingDropdownMenu}>
              {availableCategories.map((category) => (
                <div
                  key={category}
                  className={`${styles.clothingDropdownItem} ${
                    propSelectedCategory === category
                      ? styles.clothingDropdownItemSelected
                      : ""
                  }`}
                  onClick={() => handleSelect(category)}
                >
                  {categoryDisplayNames[
                    category as keyof typeof categoryDisplayNames
                  ] || category}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.mobileCategories}>
          {mobileCategories.map((category) => (
            <button
              key={category}
              className={`${styles.mobileCategoryButton} ${
                propSelectedCategory === category ? "selected" : ""
              }`}
              onClick={() => handleSelect(category)}
              disabled={!availableCategories.includes(category as any)}
            >
              {categoryDisplayNames[
                category as keyof typeof categoryDisplayNames
              ] || category}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return renderContent();
};

export default ClothingDropdown;
